import React, { useState } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Select,
    MenuItem,
    FormControl,
} from '@mui/material';
import { SampleDataBadge } from '../../common';

// Expand icon
const ExpandIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path
            d="M4 16V12M4 16H8M4 16L9 11M16 4V8M16 4H12M16 4L11 9"
            stroke="#666666"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// Refresh icon
const RefreshIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path
            d="M14.66 15.66A8 8 0 1 1 17 10"
            stroke="#666666"
            strokeWidth="1.5"
            strokeLinecap="round"
        />
        <path
            d="M17 6v4h-4"
            stroke="#666666"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

/**
 * Machines Out of Order Table
 * Shows machines that are currently out of order with pagination
 * @param {Array} data - Machine data array
 * @param {boolean} isSampleData - Whether the displayed data is sample data
 */
const MBFMachinesOutOfOrderTable = ({
    data = [],
    isSampleData = false,
    onMaximize,
    onRefresh,
    period = 'this_month',
    onPeriodChange,
    isFullscreen = false
}) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const tableData = data;

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handlePeriodChange = (event) => {
        if (onPeriodChange) {
            onPeriodChange(event.target.value);
        }
    };

    // Paginated data
    const paginatedData = tableData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // Fullscreen mode - render just the table without wrapper
    if (isFullscreen) {
        return (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <TableContainer sx={{ flex: 1 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                                <TableCell sx={{ fontWeight: 600, color: '#333', fontSize: '13px', py: 1.5 }}>
                                    Machine Name
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#333', fontSize: '13px', py: 1.5 }}>
                                    TL Name
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#333', fontSize: '13px', py: 1.5 }}>
                                    Issue
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#333', fontSize: '13px', py: 1.5 }}>
                                    Downtime
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#333', fontSize: '13px', py: 1.5 }}>
                                    Status
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedData.map((row, index) => (
                                <TableRow
                                    key={index}
                                    sx={{
                                        '&:hover': { bgcolor: '#f9f9f9' },
                                        borderBottom: '1px solid #f0f0f0',
                                    }}
                                >
                                    <TableCell sx={{ fontSize: '13px', color: '#333', py: 2 }}>
                                        {row.machineName || row.machine_name}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '13px', color: '#666', py: 2 }}>
                                        {row.tlName || row.tl_name}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '13px', color: '#666', py: 2 }}>
                                        {row.issue}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '13px', color: '#666', py: 2 }}>
                                        {row.downtime}
                                    </TableCell>
                                    <TableCell sx={{ py: 2 }}>
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                bgcolor: '#fff0f0',
                                                color: '#dc3545',
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: '4px',
                                                fontSize: '12px',
                                                fontWeight: 500,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: '6px',
                                                    height: '6px',
                                                    borderRadius: '50%',
                                                    bgcolor: '#dc3545',
                                                }}
                                            />
                                            {row.status}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={tableData.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25]}
                    sx={{
                        borderTop: '1px solid #e6e6e6',
                        '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                            fontSize: '12px',
                        },
                        '.MuiTablePagination-select': {
                            fontSize: '12px',
                        },
                    }}
                />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                bgcolor: '#ffffff',
                borderRadius: '12px',
                border: '1px solid #e6e6e6',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    bgcolor: '#ffffff',
                    borderBottom: '1px solid #e6e6e6',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography
                        sx={{
                            color: '#333333',
                            fontFamily: '"Roboto", sans-serif',
                            fontSize: '16px',
                            lineHeight: '20px',
                            fontWeight: 500,
                        }}
                    >
                        Machines Out of Order
                    </Typography>
                    {isSampleData && <SampleDataBadge absolute={false} />}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Period Filter */}
                    <FormControl size="small">
                        <Select
                            value={period}
                            onChange={handlePeriodChange}
                            sx={{
                                fontSize: '12px',
                                height: '32px',
                                minWidth: '100px',
                                '& .MuiSelect-select': {
                                    py: '6px',
                                },
                            }}
                        >
                            <MenuItem value="this_week">This week</MenuItem>
                            <MenuItem value="this_month">This month</MenuItem>
                            <MenuItem value="this_year">This year</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Refresh Button */}
                    <IconButton
                        size="small"
                        onClick={onRefresh}
                        sx={{
                            bgcolor: '#ffffff',
                            border: '1px solid #e6e6e6',
                            borderRadius: '8px',
                            padding: '6px',
                            '&:hover': { bgcolor: '#f9f9f9' },
                        }}
                    >
                        <RefreshIcon />
                    </IconButton>

                    {/* Expand Button */}
                    <IconButton
                        size="small"
                        onClick={onMaximize}
                        sx={{
                            bgcolor: '#ffffff',
                            border: '1px solid #e6e6e6',
                            borderRadius: '8px',
                            padding: '6px',
                            '&:hover': { bgcolor: '#f9f9f9' },
                        }}
                    >
                        <ExpandIcon />
                    </IconButton>
                </Box>
            </Box>

            {/* Table */}
            <TableContainer sx={{ flex: 1 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                            <TableCell sx={{ fontWeight: 600, color: '#333', fontSize: '13px', py: 1.5 }}>
                                Machine Name
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#333', fontSize: '13px', py: 1.5 }}>
                                TL Name
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#333', fontSize: '13px', py: 1.5 }}>
                                Issue
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#333', fontSize: '13px', py: 1.5 }}>
                                Downtime
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#333', fontSize: '13px', py: 1.5 }}>
                                Status
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedData.map((row, index) => (
                            <TableRow
                                key={index}
                                sx={{
                                    '&:hover': { bgcolor: '#f9f9f9' },
                                    borderBottom: '1px solid #f0f0f0',
                                }}
                            >
                                <TableCell sx={{ fontSize: '13px', color: '#333', py: 2 }}>
                                    {row.machineName || row.machine_name}
                                </TableCell>
                                <TableCell sx={{ fontSize: '13px', color: '#666', py: 2 }}>
                                    {row.tlName || row.tl_name}
                                </TableCell>
                                <TableCell sx={{ fontSize: '13px', color: '#666', py: 2 }}>
                                    {row.issue}
                                </TableCell>
                                <TableCell sx={{ fontSize: '13px', color: '#666', py: 2 }}>
                                    {row.downtime}
                                </TableCell>
                                <TableCell sx={{ py: 2 }}>
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            bgcolor: '#fff0f0',
                                            color: '#dc3545',
                                            px: 1.5,
                                            py: 0.5,
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            fontWeight: 500,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: '6px',
                                                height: '6px',
                                                borderRadius: '50%',
                                                bgcolor: '#dc3545',
                                            }}
                                        />
                                        {row.status}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
                component="div"
                count={tableData.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
                sx={{
                    borderTop: '1px solid #e6e6e6',
                    '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                        fontSize: '12px',
                    },
                    '.MuiTablePagination-select': {
                        fontSize: '12px',
                    },
                }}
            />
        </Box>
    );
};

export default MBFMachinesOutOfOrderTable;
